import { useEffect, useMemo } from 'react';
import { type MenuInputOption } from '@saviynt/design-system';

import {
  type DataProvider,
  type Field,
  type FormValues,
  type MultiSelectField,
  type SingleSelectField,
} from '../../../shared/types';
import { type EntityInitialData } from '../data-store/initialDataSlice';
import {
  injectDefaultOptions,
  normalizeDefaultValue,
} from '../utils/defaultValueUtils';

/**
 * Resolves normalized default entries against loaded options based on provider type.
 *
 * - Fixed provider: match each entry's value against loaded options; use the
 *   provider's label if found, skip if not found (no injection).
 * - HTTP/SQL/Datastore providers: all entries are returned as-is; Option Injection
 *   handles unmatched defaults in the auto-select effect.
 */
function resolveEntriesAgainstOptions(
  entries: Array<{ value: string; label: string }>,
  loadedOptions: MenuInputOption[],
  providerType: DataProvider['type'] | undefined
): MenuInputOption[] {
  if (entries.length === 0) {
    return [];
  }

  // Fixed provider: match by value, use provider's label; skip unmatched
  if (providerType === 'FIXED') {
    return entries.reduce<MenuInputOption[]>((acc, entry) => {
      const match = loadedOptions.find(
        (opt) => String(opt.value) === entry.value
      );

      if (match) {
        acc.push({ label: match.label, value: match.value });
      }

      return acc;
    }, []);
  }

  // Remote providers (HTTP/SQL/Datastore): resolve each entry against loaded options.
  // If a match is found, use the loaded option's label; otherwise use the default's label.
  return entries.map((entry) => {
    const match = loadedOptions.find(
      (opt) => String(opt.value) === entry.value
    );

    return match
      ? { label: match.label, value: match.value }
      : { label: entry.label, value: entry.value };
  });
}

function useDefaultValue(params: {
  field: SingleSelectField;
  value?: unknown;
  isLoading?: boolean;
  options?: MenuInputOption[];
  onChange?: (selection: MenuInputOption) => void;
  providerType?: DataProvider['type'];
  formValues?: FormValues;
  initialData?: EntityInitialData | null;
  /**
   * When true, skip resolving/applying field defaults (e.g. entity update: wait for
   * `/load` snapshot + Formik reset so saved values are not overwritten by defaults).
   */
  blockDefaultAutoSelect?: boolean;
}): {
  getDefaultValue: () => MenuInputOption | null;
  processedValue: MenuInputOption | null;
};

function useDefaultValue(params: {
  field: MultiSelectField;
  value?: unknown;
  isLoading?: boolean;
  options?: MenuInputOption[];
  onChange?: (selection: MenuInputOption[]) => void;
  setSelectedOptions?: (options: MenuInputOption[]) => void;
  providerType?: DataProvider['type'];
  formValues?: FormValues;
  initialData?: EntityInitialData | null;
  blockDefaultAutoSelect?: boolean;
}): {
  getDefaultValue: () => MenuInputOption[] | null;
  processedValue: MenuInputOption[] | null;
};

function useDefaultValue(params: { field: Field }): {
  getDefaultValue: () => MenuInputOption | MenuInputOption[] | null;
};

function useDefaultValue({
  field,
  value,
  isLoading,
  options = [],
  onChange,
  setSelectedOptions,
  providerType,
  formValues = {},
  initialData = null,
  blockDefaultAutoSelect = false,
}: {
  field: Field;
  value?: unknown;
  isLoading?: boolean;
  options?: MenuInputOption[];
  onChange?:
    | ((selection: MenuInputOption) => void)
    | ((selection: MenuInputOption[]) => void);
  setSelectedOptions?: (options: MenuInputOption[]) => void;
  providerType?: DataProvider['type'];
  formValues?: FormValues;
  initialData?: EntityInitialData | null;
  blockDefaultAutoSelect?: boolean;
}) {
  const resolved = useMemo(
    () =>
      normalizeDefaultValue(
        field.defaultValue,
        field.fieldType,
        formValues,
        initialData
      ),
    [field.defaultValue, field.fieldType, formValues, initialData]
  );

  const getDefaultValue = useMemo(
    () => () => {
      if (blockDefaultAutoSelect || resolved.entries.length === 0) {
        return null;
      }

      // When options are available and providerType is known, resolve against them
      if (options.length > 0 && providerType) {
        const resolvedOptions = resolveEntriesAgainstOptions(
          resolved.entries,
          options,
          providerType
        );

        if (resolvedOptions.length === 0) {
          return null;
        }

        if (field.fieldType === 'multiselect') {
          return resolvedOptions;
        }

        return resolvedOptions[0];
      }

      // Fallback: return raw entries when options aren't loaded yet
      if (field.fieldType === 'multiselect') {
        return resolved.entries.map((entry) => ({
          label: entry.label,
          value: entry.value,
        }));
      }

      const entry = resolved.entries[0];

      return { label: entry.label, value: entry.value };
    },
    [resolved, field.fieldType, options, providerType, blockDefaultAutoSelect]
  );

  const processedValue = useMemo(() => {
    if (
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return getDefaultValue();
    }

    return null;
  }, [value, getDefaultValue]);

  // Auto-select default value when options are loaded
  useEffect(() => {
    if (
      (value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)) &&
      !isLoading &&
      options.length > 0 &&
      resolved.entries.length > 0 &&
      onChange &&
      !blockDefaultAutoSelect
    ) {
      // Resolve entries against loaded options using provider-aware logic
      const resolvedOptions = resolveEntriesAgainstOptions(
        resolved.entries,
        options,
        providerType
      );

      // For Fixed provider: only select if matches were found
      if (providerType === 'FIXED') {
        if (resolvedOptions.length === 0) {
          return;
        }

        if (
          field.fieldType === 'singleselect' &&
          resolvedOptions.length === 1
        ) {
          (onChange as (selection: MenuInputOption) => void)(
            resolvedOptions[0]
          );
        } else if (field.fieldType === 'multiselect') {
          (onChange as (selection: MenuInputOption[]) => void)(resolvedOptions);
          setSelectedOptions?.(resolvedOptions);
        }

        return;
      }

      // For remote providers (HTTP/SQL/Datastore): use Option Injection for unmatched defaults
      if (providerType) {
        const injectedOptions = injectDefaultOptions(
          options,
          resolved.entries,
          providerType
        );

        // Re-resolve after injection to pick up synthetic options
        const finalResolved = resolveEntriesAgainstOptions(
          resolved.entries,
          injectedOptions,
          providerType
        );

        if (finalResolved.length === 0) {
          return;
        }

        if (field.fieldType === 'singleselect' && finalResolved.length === 1) {
          (onChange as (selection: MenuInputOption) => void)(finalResolved[0]);
        } else if (field.fieldType === 'multiselect') {
          (onChange as (selection: MenuInputOption[]) => void)(finalResolved);
          setSelectedOptions?.(finalResolved);
        }

        return;
      }

      // Fallback for when providerType is not provided (generic overload)
      if (
        field.fieldType === 'singleselect' &&
        processedValue &&
        !Array.isArray(processedValue)
      ) {
        if (options.some((opt) => opt.value === processedValue.value)) {
          (onChange as (selection: MenuInputOption) => void)(processedValue);
        }
      } else if (
        field.fieldType === 'multiselect' &&
        processedValue &&
        Array.isArray(processedValue)
      ) {
        if (
          processedValue.every((val) =>
            options.some((opt) => opt.value === val.value)
          )
        ) {
          (onChange as (selection: MenuInputOption[]) => void)(processedValue);
          setSelectedOptions?.(processedValue);
        }
      }
    }
  }, [
    value,
    isLoading,
    options,
    resolved,
    processedValue,
    onChange,
    field.fieldType,
    setSelectedOptions,
    providerType,
    blockDefaultAutoSelect,
  ]);

  return { getDefaultValue, processedValue };
}

export default useDefaultValue;
